<template>
  <benchmark-bar-group
    v-if="loading || computedGroups.length"
    title="Workforce Impact"
    :groups="computedGroups"
    :loading="loading"
  />
</template>

<script setup>
import { computed } from 'vue';
import { POLARITY, WORKFORCE_IMPACT_GROUPS } from '@/pages/analyticsBenchmark/benchmarkConfig';
import {
  buildMetricsMap,
  calculateDelta,
  formatValue,
  hasMeaningfulValue,
} from '@/pages/analyticsBenchmark/benchmarkUtils';
import BenchmarkBarGroup from '@/pages/analyticsBenchmark/components/BenchmarkBarGroup.vue';

// Hide a comparison when enrolled performs worse than non-enrolled — we don't want
// to surface metrics that imply Stream has a negative impact on the workforce.
function enrolledOutperformsBaseline(enrolledValue, baselineValue, polarity) {
  if (enrolledValue == null || baselineValue == null) {
    return true;
  }
  const diff = Math.round((enrolledValue - baselineValue) * 10) / 10;
  if (diff === 0) {
    return true;
  }
  return polarity === POLARITY.LOWER_IS_BETTER ? diff < 0 : diff > 0;
}

const props = defineProps({
  metrics: { type: Array, default: () => [] },
  sector: { type: String, default: null },
  loading: { type: Boolean, default: false },
});

const metricsMap = computed(() => buildMetricsMap(props.metrics));

function buildMetric(config) {
  const enrolledMetric = metricsMap.value[config.enrolledMetricId];
  const enrolledValue = enrolledMetric?.company_value ?? null;
  const unit = enrolledMetric?.unit || 'percent';

  let nonEnrolledValue = null;
  let averageLabel = 'Non-enrolled';
  let formattedAverage;

  if (config.nonEnrolledDisplay === 'dash') {
    formattedAverage = '—';
  } else if (config.nonEnrolledDisplay === 'sector') {
    nonEnrolledValue = enrolledMetric?.sector_value ?? null;
    averageLabel = 'Sector avg';
    formattedAverage = formatValue(nonEnrolledValue, unit);
  } else {
    const nonEnrolledMetric = config.nonEnrolledMetricId ? metricsMap.value[config.nonEnrolledMetricId] : null;
    nonEnrolledValue = nonEnrolledMetric?.company_value ?? null;
    formattedAverage = formatValue(nonEnrolledValue, unit);
  }

  const isComparison = config.nonEnrolledDisplay !== 'dash';
  const delta = isComparison
    ? calculateDelta(enrolledValue, nonEnrolledValue, {
        deltaFormat: config.deltaFormat,
        polarity: config.polarity,
        unit,
      })
    : { raw: null, formatted: '—', colorClass: 'delta-neutral' };

  const isPercentage = unit === 'percent';
  const maxValue = isPercentage ? 100 : Math.max(0, enrolledValue || 0, nonEnrolledValue || 0) * 1.2 || 100;

  const shouldHide = isComparison && !enrolledOutperformsBaseline(enrolledValue, nonEnrolledValue, config.polarity);

  return {
    id: config.enrolledMetricId,
    label: config.label,
    description: enrolledMetric?.description || null,
    tooltipId: `wi-${config.enrolledMetricId}`,
    value: enrolledValue,
    average: nonEnrolledValue,
    maxValue,
    formattedValue: formatValue(enrolledValue, unit),
    formattedAverage,
    deltaFormatted: delta.formatted,
    deltaColorClass: delta.colorClass,
    averageLabel,
    shouldHide,
  };
}

const computedGroups = computed(() =>
  WORKFORCE_IMPACT_GROUPS.map(group => ({
    name: group.name,
    metrics: group.metrics.map(buildMetric).filter(m => hasMeaningfulValue(m.value) && !m.shouldHide),
  })).filter(group => group.metrics.length > 0),
);

const hasData = computed(() => !props.loading && computedGroups.value.length > 0);

function getExportTable() {
  if (!hasData.value) {
    return null;
  }
  const rows = [];
  for (const group of computedGroups.value) {
    for (const metric of group.metrics) {
      rows.push([
        group.name,
        metric.label,
        metric.formattedValue,
        metric.averageLabel,
        metric.formattedAverage,
        metric.deltaFormatted,
      ]);
    }
  }
  return {
    headers: ['Group', 'Metric', 'Enrolled', 'Comparison', 'Comparison value', 'Delta'],
    rows,
  };
}

defineExpose({
  hasData,
  getExportTable,
  getPdfData: () => ({
    benchmarkPanel: {
      title: 'Workforce Impact',
      subtitle: props.sector ? `Enrolled employees in the ${props.sector} sector vs comparison group` : null,
      groups: computedGroups.value,
    },
  }),
});
</script>
